import { DashboardLayoutContainer } from 'components/Layouts/DashboardLayout';
import { Box } from '@mui/material';
import {
  IconGraphy,
  Typography,
  colors,
  Tab,
  Grid,
  Button,
  Table,
  Loading,
} from '@leapeasy/ui-kit';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { openDetails } from 'store/actions/uiActions';
import { getInvitePropertyAction, inviteUserAction } from 'store/actions/inviteActions';
import { getLandlordDetailAction } from 'store/actions/userActions';
import { failedNotif, successNotif } from 'store/actions/notifActions';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { InviteComposePanel } from './InviteComposePanel';
import { getInviteUserColumns } from './InviteTableColumns';
import { InvitePropertySelectAllBar } from './InvitePropertySelectAllBar';
import { getInviteProperty } from 'store/sagas/APIs/invite';
import { ListExportScopeModal } from 'components/Tables/shared/ListExportScopeModal';
import { fetchAllPaginated } from 'utils/exportPagination';
import {
  pageHeaderSx,
  backIconWrapperSx,
  pageTitleStyle,
  contentContainerSx,
  actionRowSx,
  helperColumnSx,
  actionColumnSx,
  helperTextStyle,
  selectedCountStyle,
  composeButtonStyle,
  tableWrapperSx,
} from './InvitePage.styles';
import { GlobalUXState } from 'components/GlobalUXState';
import { useClampListPagination } from 'hooks/useClampListPagination';

const TABS = [
  { title: 'Full Portfolio', role: 'vp' },
  { title: 'Multi-Site', role: 'rm' },
  { title: 'Property Manager', role: 'pm' },
];

const INVITE_PROPERTY_LIST_FILTER_DEFAULT = {
  searchText: '',
  options: {},
  searchPlaceholder: 'Search Property..',
};

export const InvitePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { landlordId } = useParams();
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [openComposer, setOpenComposer] = useState(false);
  const [awaitingInviteResponse, setAwaitingInviteResponse] = useState(false);
  const [sortOptions, setSortOptions] = useState({});
  const [filter, setFilter] = useState(INVITE_PROPERTY_LIST_FILTER_DEFAULT);
  const inviteListTableRef = useRef(null);
  const selectedBuildingCacheRef = useRef(new Map());
  const selectedAllIdsRef = useRef([]);
  const [selectAllState, setSelectAllState] = useState({ scopeKey: null, selected: false });
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [inviteListExportOpen, setInviteListExportOpen] = useState(false);

  const [pagination, setPagination] = useState({
    pageNumber: 0,
    rowsPerPage: 12,
    maxVisiblePageItems: 6,
    onUpdate: (value) => {
      if (value === -1) value = 0;
      setPagination((prev) => ({ ...prev, pageNumber: value }));
    },
    showPageNumberInput: 6,
  });

  const details = useSelector((state) => state.getIn(['ui', 'details']));
  const landlordDetail = useSelector((state) => state.getIn(['user', 'landlord_detail'])) || null;
  const inviteProperties = useSelector((state) => state.getIn(['invite', 'inviteProperty']));
  const loading = useSelector((state) => state.getIn(['auth', 'loading']));
  const { hasSuccess: hasSuccessNotification, hasError: hasErrorNotification } =
    useNotificationMessages();
  const currentUserState = useSelector((state) => state.getIn(['auth', 'currentUser']));

  const detailsJSON = details ? details.toJS() : null;
  const detailsType = details ? details.get('type') : null;
  const landlordDetailJSON = landlordDetail ? landlordDetail.toJS() : null;
  const landlordData =
    landlordDetailJSON || (detailsJSON?.type === 'landlord' ? detailsJSON.data : null);
  const invitePropertiesJSON = inviteProperties ? inviteProperties.toJS() : null;
  const currentUser = currentUserState ? currentUserState.toJS() : null;
  const fromEmail =
    currentUser?.email || JSON.parse(localStorage.getItem('user') || '{}')?.email || '';
  const selectedRole = TABS[selectedTab].role;
  const fallbackLandlordId =
    details?.getIn?.(['data', 'sf_landlord_id']) ||
    details?.getIn?.(['data', 'landlord_id']) ||
    details?.getIn?.(['data', 'id']) ||
    null;
  const selectedLandlordId =
    landlordDetail?.get?.('sf_landlord_id') ||
    landlordDetail?.get?.('landlord_id') ||
    landlordDetail?.get?.('id') ||
    fallbackLandlordId ||
    landlordId ||
    null;
  const selectionScopeKey = `${selectedLandlordId || ''}:${selectedRole}:${filter.searchText || ''}`;

  const onClickBack = useCallback(() => {
    dispatch(openDetails(null));
    navigate('/user/invite-new-user');
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!landlordId) {
      if (!detailsJSON || detailsType !== 'landlord') {
        dispatch(failedNotif('Operator ID is missing.'));
        onClickBack();
      }
      return;
    }
    dispatch(getLandlordDetailAction(landlordId));
  }, [dispatch, landlordId, detailsType, onClickBack]);

  useEffect(() => {
    setSelectedProperties([]);
    selectedBuildingCacheRef.current.clear();
    selectedAllIdsRef.current = [];
    setSelectAllState({ scopeKey: null, selected: false });
    setFilter(INVITE_PROPERTY_LIST_FILTER_DEFAULT);
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [selectedRole]);

  useEffect(() => {
    setPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [filter.searchText]);

  useEffect(() => {
    if (!selectedLandlordId) return;

    dispatch(
      getInvitePropertyAction({
        landlordId: selectedLandlordId,
        inviteRole: selectedRole,
        offset: pagination.pageNumber * pagination.rowsPerPage,
        limit: pagination.rowsPerPage,
        sortOptions,
        filter: { searchText: (filter.searchText || '').trim() },
      }),
    );
  }, [
    selectedLandlordId,
    selectedRole,
    pagination.pageNumber,
    pagination.rowsPerPage,
    sortOptions,
    filter.searchText,
    dispatch,
  ]);

  const buildingRows = useMemo(() => invitePropertiesJSON?.data || [], [invitePropertiesJSON]);
  useEffect(() => {
    buildingRows.forEach((building) => {
      selectedBuildingCacheRef.current.set(building.building_id, building);
    });
  }, [buildingRows]);

  useClampListPagination({
    pagination,
    setPagination,
    totalItems: Number(invitePropertiesJSON?.total) || 0,
    isLoading: Boolean(invitePropertiesJSON?.isLoading),
  });

  const selectedBuildings = useMemo(
    () =>
      selectedProperties
        .map((buildingId) => selectedBuildingCacheRef.current.get(buildingId))
        .filter(Boolean),
    [buildingRows, selectedProperties],
  );
  const suggestedToEmail = useMemo(
    () =>
      selectedRole === 'pm'
        ? selectedBuildings
            .map((item) => item.email_address)
            .filter(Boolean)
            .join(', ')
        : '',
    [selectedBuildings, selectedRole],
  );
  const visibleBuildingIds = useMemo(
    () => buildingRows.map((building) => building.building_id),
    [buildingRows],
  );
  const selectedVisibleCount = useMemo(
    () => visibleBuildingIds.filter((id) => selectedProperties.includes(id)).length,
    [visibleBuildingIds, selectedProperties],
  );
  const allVisibleSelected =
    visibleBuildingIds.length > 0 && selectedVisibleCount === visibleBuildingIds.length;
  const partiallySelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleBuildingIds.length;
  const allSelected =
    (selectAllState.scopeKey === selectionScopeKey && selectAllState.selected) ||
    allVisibleSelected;

  const onToggleProperty = useCallback(
    (propertyId) => {
      if (
        selectAllState.scopeKey === selectionScopeKey &&
        selectedAllIdsRef.current.includes(propertyId)
      ) {
        setSelectAllState((existing) => ({ ...existing, selected: false }));
      }
      setSelectedProperties((existing) =>
        existing.includes(propertyId)
          ? existing.filter((id) => id !== propertyId)
          : [...existing, propertyId],
      );
    },
    [selectAllState.scopeKey, selectionScopeKey],
  );

  const onToggleAllProperties = useCallback(() => {
    if (selectAllLoading) return;
    const totalRecords = Number(invitePropertiesJSON?.total) || buildingRows.length;
    if (totalRecords === 0 || !selectedLandlordId) return;

    const fetchMatchingBuildings = async () => {
      setSelectAllLoading(true);
      try {
        const matchingBuildings = await fetchAllPaginated({
          totalRecords,
          fetchPage: ({ offset, limit }) =>
            getInviteProperty({
              landlordId: selectedLandlordId,
              inviteRole: selectedRole,
              offset,
              limit,
              sortOptions,
              filter: { searchText: (filter.searchText || '').trim() },
            }),
        });
        matchingBuildings.forEach((building) => {
          selectedBuildingCacheRef.current.set(building.building_id, building);
        });
        const matchingIds = matchingBuildings.map((building) => building.building_id);
        const deselectingAll =
          selectAllState.scopeKey === selectionScopeKey && selectAllState.selected;
        if (deselectingAll) {
          selectedAllIdsRef.current = [];
          setSelectAllState({ scopeKey: selectionScopeKey, selected: false });
        } else {
          selectedAllIdsRef.current = matchingIds;
          setSelectAllState({ scopeKey: selectionScopeKey, selected: true });
        }
        setSelectedProperties((existing) => {
          if (deselectingAll) {
            return existing.filter((id) => !matchingIds.includes(id));
          }
          return [...new Set([...existing, ...matchingIds])];
        });
      } catch (error) {
        dispatch(failedNotif('Unable to select all matching properties. Please try again.'));
      } finally {
        setSelectAllLoading(false);
      }
    };

    fetchMatchingBuildings();
  }, [
    buildingRows.length,
    dispatch,
    filter.searchText,
    invitePropertiesJSON?.total,
    selectedLandlordId,
    selectedRole,
    selectAllLoading,
    selectAllState,
    selectionScopeKey,
    sortOptions,
  ]);

  const tableData = useMemo(
    () =>
      buildingRows.map((building) => [
        building.building_id,
        building.name || '-',
        building.building_type || 'Undefined',
        building.phone || '-',
        building.billingStreet || '-',
        building.email_address || '-',
        building.total_of_units || 0,
        building.student_housing,
        building.building_id,
      ]),
    [buildingRows],
  );

  const onOpenComposer = () => {
    if (selectedProperties.length === 0) {
      return;
    }
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingInviteResponse(false);
    setOpenComposer(true);
  };

  const onDiscardComposer = () => {
    setAwaitingInviteResponse(false);
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setOpenComposer(false);
  };

  useEffect(() => {
    if (!awaitingInviteResponse || !hasSuccessNotification) {
      return;
    }
    setOpenComposer(false);
    setAwaitingInviteResponse(false);
    setSelectedProperties([]);
    selectedBuildingCacheRef.current.clear();
  }, [awaitingInviteResponse, hasSuccessNotification]);

  useEffect(() => {
    if (awaitingInviteResponse && hasErrorNotification) {
      setAwaitingInviteResponse(false);
    }
  }, [awaitingInviteResponse, hasErrorNotification]);

  const onSendInvite = ({ toEmail, subject, content }) => {
    const resolvedLandlordId =
      landlordData?.sf_landlord_id ||
      landlordData?.landlord_id ||
      landlordData?.id ||
      selectedLandlordId;
    const recipients = toEmail
      .split(',')
      .map((email) => email.trim())
      .filter(Boolean);

    if (!resolvedLandlordId || recipients.length === 0 || selectedProperties.length === 0) {
      return;
    }

    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    setAwaitingInviteResponse(true);
    dispatch(
      inviteUserAction({
        userRole: selectedRole,
        toEmail: recipients,
        emailSubject: subject || "You've been Invited to the Leap Portal",
        emailContent: content || 'You have been invited to the Leap Portal.',
        landlordId: resolvedLandlordId,
        property: selectedProperties,
      }),
    );
  };

  const onClickInviteUserDetail = useCallback(
    (buildingId) => {
      const selectedBuilding = buildingRows.find((building) => building.building_id === buildingId);
      if (!selectedBuilding) return;
      const resolvedLandlordId =
        selectedBuilding?.sf_landlord_id ||
        selectedBuilding?.landlord_id ||
        landlordData?.sf_landlord_id ||
        landlordData?.landlord_id ||
        selectedLandlordId;
      if (!resolvedLandlordId) return;

      dispatch(
        openDetails({
          type: 'landlord',
          name: 'Invite users details',
          icon: 'Users.User',
          data: selectedBuilding,
        }),
      );
      navigate(`/user/invite-new-user/${resolvedLandlordId}/detail/report`);
    },
    [buildingRows, landlordData, selectedLandlordId, dispatch, navigate],
  );

  const handleInviteListExportData = useCallback(async () => {
    if (!selectedLandlordId) return [];
    const totalRecords = Number(invitePropertiesJSON?.total) || buildingRows.length || 0;
    if (totalRecords === 0) return [];
    try {
      const rows = await fetchAllPaginated({
        totalRecords,
        fetchPage: ({ offset, limit }) =>
          getInviteProperty({
            landlordId: selectedLandlordId,
            inviteRole: selectedRole,
            offset,
            limit,
            sortOptions,
            filter: { searchText: (filter.searchText || '').trim() },
          }),
      });
      return rows.map((building) => [
        building.building_id,
        building.name || '-',
        building.building_type || 'Undefined',
        building.phone || '-',
        building.billingStreet || '-',
        building.email_address || '-',
        building.total_of_units || 0,
        building.student_housing,
        building.building_id,
      ]);
    } catch (e) {
      throw new Error('Unable to export invite properties right now.');
    }
  }, [
    selectedLandlordId,
    selectedRole,
    invitePropertiesJSON?.total,
    buildingRows.length,
    sortOptions,
    filter.searchText,
  ]);

  const columns = useMemo(
    () =>
      getInviteUserColumns({
        selectedProperties,
        onToggleProperty,
        onClickInviteUserDetail,
      }),
    [selectedProperties, onToggleProperty, onClickInviteUserDetail],
  );

  const shouldShowInitialLoading = Boolean(selectedLandlordId) && !invitePropertiesJSON;
  const hasSearch = Boolean(`${filter.searchText || ''}`.trim());
  return (
    <DashboardLayoutContainer>
      <InviteComposePanel
        open={openComposer}
        fromEmail={fromEmail}
        suggestedToEmail={suggestedToEmail}
        selectedBuildings={selectedRole === 'pm' ? selectedBuildings : []}
        loading={loading && awaitingInviteResponse}
        disableSend={selectedProperties.length === 0}
        onClose={onDiscardComposer}
        onSend={onSendInvite}
      />
      <Box sx={pageHeaderSx}>
        <Box sx={backIconWrapperSx}>
          <IconGraphy icon="Arrow.ArrowBack" onClick={onClickBack} />
        </Box>
        <Typography variant="h3" style={{ color: colors.purple[900], ...pageTitleStyle }}>
          Invite New Users {landlordData?.name ? `- ${landlordData.name}` : ''}
        </Typography>
      </Box>

      <Box padding="24px 12px" borderRadius="12px" mt={1} sx={contentContainerSx}>
        <Grid>
          {TABS.map((tab, index) => (
            <Tab
              key={tab.role}
              title={tab.title}
              isSelected={selectedTab === index}
              onClick={() => setSelectedTab(index)}
            />
          ))}
        </Grid>

        <Box marginTop="20px">
          <Box sx={actionRowSx}>
            <Box sx={helperColumnSx}>
              <Typography variant="body2" style={helperTextStyle}>
                Choose Associated Properties, and Click 'Send Invite'
              </Typography>
            </Box>
            <Box sx={actionColumnSx}>
              {selectedProperties.length > 0 && (
                <>
                  <Typography variant="body2" style={selectedCountStyle}>
                    {selectedProperties.length} row(s) selected
                  </Typography>
                  <Button variant="primary" onClick={onOpenComposer} style={composeButtonStyle}>
                    Send Invite
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <Box sx={tableWrapperSx}>
            {shouldShowInitialLoading ? (
              <Loading size="large" label="Loading invite form..." />
            ) : (
              <>
                {buildingRows.length > 0 && (
                  <InvitePropertySelectAllBar
                    allSelected={allSelected}
                    partiallySelected={partiallySelected}
                    onToggle={onToggleAllProperties}
                    visibleCount={invitePropertiesJSON?.total || buildingRows.length}
                    hasSearch={hasSearch}
                    loading={selectAllLoading}
                  />
                )}
                <Table
                  showEmptyState
                  emptyContent={
                    <GlobalUXState
                      variant={hasSearch ? 'noResults' : 'noData'}
                      compact
                      description={
                        hasSearch
                          ? 'No properties match your search.'
                          : 'There are no properties available to invite for this operator.'
                      }
                    />
                  }
                  filter={filter}
                  onChangeFilter={setFilter}
                  ref={inviteListTableRef}
                  onExportButtonClick={() => setInviteListExportOpen(true)}
                  onRequestExportData={selectedLandlordId ? handleInviteListExportData : undefined}
                  onRequestFullExportData={
                    selectedLandlordId ? handleInviteListExportData : undefined
                  }
                  data={tableData}
                  columns={columns}
                  sortOptions={sortOptions}
                  onChangeSort={setSortOptions}
                  pagination={{
                    ...pagination,
                    totalItems: invitePropertiesJSON?.total || buildingRows.length,
                    totalPages: Math.ceil(
                      (invitePropertiesJSON?.total || buildingRows.length) / pagination.rowsPerPage,
                    ),
                  }}
                  onChangeRowsPerPage={(value) =>
                    setPagination({
                      ...pagination,
                      rowsPerPage: value,
                      pageNumber: 0,
                    })
                  }
                  onChangePagination={setPagination}
                />
                <ListExportScopeModal
                  open={inviteListExportOpen}
                  onClose={() => setInviteListExportOpen(false)}
                  tableRef={inviteListTableRef}
                  title="Invite properties"
                  currentPageRowCount={tableData.length}
                  paginationTotalItems={invitePropertiesJSON?.total || buildingRows.length}
                  enableFiltered={Boolean(selectedLandlordId)}
                  fullDatasetTotal={invitePropertiesJSON?.total || buildingRows.length}
                  enableFull={Boolean(selectedLandlordId)}
                  enableSelected={false}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </DashboardLayoutContainer>
  );
};
